import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import "../views/admin-dashboard.css";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function AdminDashboard() {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [sexFilter, setSexFilter] = useState("");
    const [fightTypeFilter, setFightTypeFilter] = useState("");
    const [sortBy, setSortBy] = useState("cognome");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 40;
    const uniqueCategories = [...new Set(registrations.map((r) => r.categoria).filter(Boolean))];
    const uniqueFightTypes = [...new Set(registrations.map((r) => r.tipo_gara).filter(Boolean))];

    const filteredRegistrations = registrations
        .filter((item) => {
            const fullName = `${item.nome} ${item.cognome}`.toLowerCase();
            const term = searchTerm.toLowerCase();

            const matchesSearch =
                fullName.includes(term) ||
                (item.email || "").toLowerCase().includes(term) ||
                (item.telefono || "").toLowerCase().includes(term);

            const matchesCategory = !categoryFilter || item.categoria === categoryFilter;
            const matchesSex = !sexFilter || item.sesso === sexFilter;
            const matchesFightType = !fightTypeFilter || item.tipo_gara === fightTypeFilter;

            return matchesSearch && matchesCategory && matchesSex && matchesFightType;
        })
        .sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];

            if (sortBy === "peso" || sortBy === "eta" || sortBy === "eta_calcolata") {
                aValue = Number(aValue || 0);
                bValue = Number(bValue || 0);
            } else {
                aValue = String(aValue || "").toLowerCase();
                bValue = String(bValue || "").toLowerCase();
            }

            if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
            if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });

    const toggleSelection = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (
            filteredRegistrations.length > 0 &&
            filteredRegistrations.every((r) => selectedIds.includes(r.id))
        ) {
            setSelectedIds((prev) =>
                prev.filter((id) => !filteredRegistrations.some((r) => r.id === id))
            );
        } else {
            const filteredIds = filteredRegistrations.map((r) => r.id);
            setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
        }
    };

    const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRegistrations = filteredRegistrations.slice(startIndex, endIndex);

    useEffect(() => {
        const loadRegistrations = async () => {

            try {

                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/registrations`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("adminToken")}`
                    }
                });

                if (!response.ok) {
                    window.location.href = "/admin";
                    return;
                }

                const data = await response.json();

                if (!data.success) {
                    window.location.href = "/admin";
                    return;
                }

                setRegistrations(data.registrations || []);

            } catch (error) {
                console.error("Errore caricamento iscrizioni:", error);
            } finally {
                setLoading(false);
            }
        };

        loadRegistrations();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, categoryFilter, sexFilter, fightTypeFilter, sortBy, sortOrder]);

    const handleLogout = async () => {
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/admin/logout`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("adminToken")}`
                }
            });
        } catch (error) {
            console.error("Errore logout:", error);
        } finally {
            localStorage.removeItem("adminToken");
            window.location.href = "/admin";
        }
    };

    const handleDeleteSelected = async () => {


        if (selectedIds.length === 0) return;

        const conferma = window.confirm(`Vuoi davvero eliminare ${selectedIds.length} iscrizion${selectedIds.length === 1 ? "e" : "i"}?`);
        if (!conferma) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/registrations`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("adminToken")}`
                },
                body: JSON.stringify({ ids: selectedIds })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                alert(data.error || "Errore durante l'eliminazione");
                return;
            }

            setRegistrations((prev) => prev.filter((item) => !selectedIds.includes(item.id)));
            setSelectedIds([]);
        } catch (error) {
            console.error("Errore eliminazione iscrizioni:", error);
            alert("Errore di connessione al server");
        }
    };

    const savePdf = () => {
        if (filteredRegistrations.length === 0) {
            alert("Nessun dato da esportare");
            return;
        }

        const rows = filteredRegistrations.map((item) => [
            item.nome ?? "-",
            item.cognome ?? "-",
            item.email ?? "-",
            item.telefono ?? "-",
            item.eta_calcolata ?? item.eta ?? "-",
            item.sesso ?? "-",
            item.peso ?? "-",
            item.categoria ?? "-",
            item.tipo_gara ?? "-",
            item.fascia_peso ?? "-",
            item.esperienza ?? "-",
            item.cintura ?? "-"
        ]);

        const img = new Image();
        img.src = "/assets/logoblack.png";

        img.onload = () => {
            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4"
            });

            doc.setFontSize(16);
            doc.addImage(img, "PNG", 14, 8, 30, 30);
            doc.text("Elenco iscrizioni:", 48, 28);

            const activeFilters = [
                searchTerm ? `Ricerca: ${searchTerm}` : null,
                categoryFilter ? `Categoria: ${categoryFilter}` : null,
                sexFilter ? `Sesso: ${sexFilter}` : null,
                fightTypeFilter ? `Tipo gara: ${fightTypeFilter}` : null
            ].filter(Boolean).join(" | ");

            if (activeFilters) {
                doc.text(`Filtri: ${activeFilters}`, 14, 40);
            }

            autoTable(doc, {
                startY: 45,
                head: [[
                    "Nome", "Cognome", "Email", "Telefono", "Età",
                    "Sesso", "Peso", "Categoria", "Tipo gara",
                    "Fascia peso", "Esperienza", "Cintura"
                ]],
                body: rows,
                styles: {
                    fontSize: 8,
                    cellPadding: 2.5,
                    overflow: "linebreak",
                    valign: "middle",
                    textColor: [30, 30, 30]
                },
                headStyles: {
                    fillColor: [15, 23, 42],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                columnStyles: {
                    2: { cellWidth: 42 },
                    3: { cellWidth: 28 }
                },
                margin: { top: 20, right: 10, bottom: 10, left: 10 },
                didDrawPage: (data) => {
                    const pageCount = doc.getNumberOfPages();
                    doc.setFontSize(9);
                    doc.text(
                        `Pagina ${data.pageNumber} di ${pageCount}`,
                        data.settings.margin.left,
                        doc.internal.pageSize.height - 6
                    );
                }
            });

            doc.save("iscrizioni-filtrate.pdf");
        }
        img.onerror = (e) => {
            alert("Logo non trovato");
        }
    };


    return (
        <div className="admin-dashboard-page">
            <div className="admin-dashboard-header">
                <h1>Admin Panel</h1>
                <button onClick={handleLogout}>Logout</button>
            </div>

            <div className="admin-dashboard-content">
                {loading ? (
                    <p>Caricamento...</p>
                ) : (
                    <>
                        <div className="admin-table-actions">
                            <span>
                                {selectedIds.length} selezionati
                            </span>
                            <button
                                onClick={handleDeleteSelected}
                                disabled={selectedIds.length === 0} className={"deleteBtn"}
                            >
                                <p className={"deleteBtnText"}>Elimina selezionati</p>
                            </button>
                        </div>
                        <div className="admin-filters">

                            <div className="search-box">
                                <Search size={18} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Cerca"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                <option value="">Categoria</option>
                                {uniqueCategories.map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>

                            <select value={sexFilter} onChange={(e) => setSexFilter(e.target.value)}>
                                <option value="">Sesso</option>
                                <option value="maschio">Maschio</option>
                                <option value="femmina">Femmina</option>
                            </select>

                            <select value={fightTypeFilter} onChange={(e) => setFightTypeFilter(e.target.value)}>
                                <option value="">Tipo gara</option>
                                {uniqueFightTypes.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>

                            <button
                                className="reset-filters"
                                onClick={() => {
                                    setSearchTerm("");
                                    setCategoryFilter("");
                                    setSexFilter("");
                                    setFightTypeFilter("");
                                }}
                            >Reset</button>

                            <button className="export-btn"
                            onClick={() => {savePdf()}}
                            >Esporta PDF</button>
                        </div>
                        <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={
                                            filteredRegistrations.length > 0 &&
                                            filteredRegistrations.every((r) => selectedIds.includes(r.id))
                                        }
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>Nome</th>
                                <th>Cognome</th>
                                <th>Email</th>
                                <th>Telefono</th>
                                <th>Età</th>
                                <th>Sesso</th>
                                <th>Peso</th>
                                <th>Categoria</th>
                                <th>Tipo gara</th>
                                <th>Fascia peso</th>
                                <th>Esperienza</th>
                                <th>Cintura</th>
                                <th>Certificato</th>
                            </tr>
                            </thead>
                            <tbody>
                            {paginatedRegistrations.map((item) => (
                                <tr key={item.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(item.id)}
                                            onChange={() => toggleSelection(item.id)}
                                        />
                                    </td>
                                    <td>{item.nome}</td>
                                    <td>{item.cognome}</td>
                                    <td>{item.email}</td>
                                    <td>{item.telefono}</td>
                                    <td>{item.eta_calcolata || item.eta}</td>
                                    <td>{item.sesso}</td>
                                    <td>{item.peso}</td>
                                    <td>{item.categoria}</td>
                                    <td>{item.tipo_gara}</td>
                                    <td>{item.fascia_peso}</td>
                                    <td>{item.esperienza}</td>
                                    <td>{item.cintura}</td>
                                    <td> {item.certificato ? (
                                        <button
                                            className="certificate-link"
                                            onClick={async () => {
                                                try {
                                                    const res = await fetch(
                                                        `${process.env.REACT_APP_API_URL}/api/admin/certificates?path=${encodeURIComponent(item.certificato)}`,
                                                        {
                                                            headers: {
                                                                Authorization: `Bearer ${localStorage.getItem("adminToken")}`
                                                            }
                                                        }
                                                    );

                                                    const data = await res.json();

                                                    if (!res.ok || !data.success) {
                                                        alert(data.error || "Errore apertura certificato");
                                                        return;
                                                    }

                                                    window.open(data.url, "_blank");

                                                } catch (err) {
                                                    console.error(err);
                                                    alert("Errore di connessione");
                                                }
                                            }}
                                        >
                                            Apri
                                        </button>
                                    ) : (
                                        "-"
                                    )}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                        <div className="admin-pagination">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Prec.
                            </button>

                            <span>{currentPage} di {totalPages || 1}</span>

                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                            >
                                Succ.
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}